import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { flushProtectedSaves } from "@/lib/useProtectedSync";
import { stockLedger } from "@/lib/stockLedger";
import { toast } from "sonner";

const button = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50";
const primary = "rounded-lg bg-[#1C64B0] px-4 py-2 text-sm font-semibold text-white hover:bg-[#154E8A] disabled:opacity-50";
const toNum = (value) => Number(String(value).replace(",", "."));

export const GrondTelOvernemen = ({ xl }) => {
  const { grondLedger, kgf, onStockCountSaved } = xl;
  const [naam, setNaam] = useState("");
  const [rows, setRows] = useState([{ tracecode: "", geteld: "" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const groups = useMemo(() => grondLedger.catalog.filter((g) => g.naam).sort((a, b) => String(a.naam).localeCompare(String(b.naam), "nl")), [grondLedger.catalog]);
  const bestaandeLots = useMemo(() => {
    if (!naam) return [];
    const grp = groups.find((g) => g.names.includes(naam));
    const namen = grp ? grp.names : [naam];
    return grondLedger.lots.filter((l) => l.active && l.rest !== 0 && l.tracecode && l.names.some((n) => namen.includes(n)));
  }, [naam, groups, grondLedger.lots]);

  const kiesNaam = (value) => {
    setNaam(value);
    setError("");
    const grp = groups.find((g) => g.names.includes(value));
    const namen = grp ? grp.names : [value];
    const lots = grondLedger.lots.filter((l) => l.active && l.rest !== 0 && l.tracecode && l.names.some((n) => namen.includes(n)));
    setRows(lots.length ? lots.map((l) => ({ tracecode: l.tracecode, geteld: "" })) : [{ tracecode: "", geteld: "" }]);
  };

  const setRow = (index, field, value) => { setRows((list) => list.map((r, i) => (i === index ? { ...r, [field]: value } : r))); setError(""); };
  const addRow = () => setRows((list) => [...list, { tracecode: "", geteld: "" }]);
  const removeRow = (index) => setRows((list) => (list.length > 1 ? list.filter((_, i) => i !== index) : list));

  const overnemen = async () => {
    if (!naam) return setError("Kies eerst een grondstof.");
    const gevuld = rows.map((r) => ({ tracecode: String(r.tracecode || "").trim(), geteld: r.geteld })).filter((r) => r.tracecode !== "" || String(r.geteld).trim() !== "");
    if (!gevuld.length) return setError("Vul minstens één tracecode met getelde hoeveelheid in.");
    for (const r of gevuld) {
      const g = toNum(r.geteld);
      if (!Number.isFinite(g) || g < 0) return setError("Elke telling moet een getal van 0 kg of meer zijn.");
      if (!r.tracecode && g > 0) return setError("Een positieve telling vereist een tracecode. Vul de tracecode/lot van de partij in — bij grondstoffen is dit verplicht (geen automatische keuze).");
    }
    const codes = gevuld.filter((r) => r.tracecode).map((r) => r.tracecode);
    if (new Set(codes).size !== codes.length) return setError("Dezelfde tracecode staat meerdere keren in de telling. Elke tracecode mag maar één keer voorkomen.");
    setBusy(true); setError("");
    try {
      await flushProtectedSaves();
      const ctx = await api.get("/voorraad/telling-context/Grondstof");
      const ledger = stockLedger(ctx.data.data, "Grondstof");
      const cat = ledger.catalog.find((m) => m.names.includes(naam));
      if (!cat) { setError("Deze grondstof staat niet in de stamgegevens. Voeg hem eerst toe bij Stamgegevens."); return; }
      const lines = gevuld.map((r) => {
        const lot = ledger.lots.find((l) => l.material === cat.material && l.tracecode === r.tracecode);
        return { material: cat.material, naam: cat.naam, artikelnummer: cat.artikelnummer || "", tracecode: r.tracecode, datum: lot?.datum || "", leverancier: lot?.leverancier || cat.leverancier || "", prijs: lot?.prijs == null ? (cat.prijs || 0) : lot.prijs, theoretisch: lot?.rest || 0, geteld: toNum(r.geteld) };
      });
      const res = await api.post("/voorraad/tellingen/Grondstof", { id: crypto.randomUUID(), token: ctx.data.token, lines });
      onStockCountSaved(res.data.record);
      toast.success(`Telling van ${naam} overgenomen (${lines.length} tracecode${lines.length > 1 ? "s" : ""}). Vastgelegd als nieuw telmoment en volledig traceerbaar.`);
      setNaam(""); setRows([{ tracecode: "", geteld: "" }]);
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="vg-quick-adopt" className="rounded-xl border border-slate-200 bg-white p-5 mb-6">
      <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-400">Snel tellen &amp; overnemen (per grondstof)</div>
      <p className="mb-4 max-w-3xl text-sm text-slate-500">Kies een grondstof, vul per tracecode de getelde hoeveelheid in en klik op <b>Neem over</b>. De telling wordt veilig als nieuw telmoment vastgelegd — zonder correcties en dus zonder dat er negatieve standen kunnen ontstaan. Een positieve telling vereist altijd een tracecode; bij grondstoffen kies je zelf de partij — er is <b>geen</b> automatische (FIFO) afboeking.</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-slate-600"><span>Grondstof</span>
          <select data-testid="vg-quick-naam" aria-label="Kies grondstof" className="w-72 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800" value={naam} onChange={(e) => kiesNaam(e.target.value)}>
            <option value="">Kies grondstof…</option>
            {groups.map((g) => <option key={g.material || g.naam} value={g.naam}>{g.artikelnummer ? `${g.artikelnummer} · ` : ""}{g.naam}</option>)}
          </select>
        </label>
      </div>
      {naam && (
        <div className="mt-4 space-y-3">
          {bestaandeLots.length > 0 && <p data-testid="vg-quick-hint" className="text-xs text-slate-400">Bekende tracecodes met huidige stand zijn voor-ingevuld. Vul de werkelijke telling in of voeg een nieuwe tracecode toe.</p>}
          <div className="space-y-2">
            {rows.map((r, i) => {
              const lot = bestaandeLots.find((l) => l.tracecode === r.tracecode);
              return (
                <div key={i} data-testid={`vg-quick-row-${i}`} className="flex flex-wrap items-end gap-3">
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600"><span>Tracecode / lot</span>
                    <input data-testid={`vg-quick-trace-${i}`} aria-label="Tracecode" className="w-48 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" placeholder="Tracecode op partij" value={r.tracecode} onChange={(e) => setRow(i, "tracecode", e.target.value)} />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600"><span>Geteld kg{lot ? ` · nu ${kgf(lot.rest)}` : ""}</span>
                    <input data-testid={`vg-quick-geteld-${i}`} aria-label="Geteld kg" inputMode="decimal" className="w-32 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="kg" value={r.geteld} onChange={(e) => setRow(i, "geteld", e.target.value)} />
                  </label>
                  <button data-testid={`vg-quick-remove-${i}`} className={button} disabled={rows.length === 1} onClick={() => removeRow(i)}>Verwijder</button>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 pt-1">
            <button data-testid="vg-quick-add-trace" className={button} onClick={addRow}>+ Tracecode toevoegen</button>
            <button data-testid="vg-quick-adopt-btn" className={primary} disabled={busy} onClick={overnemen}>{busy ? "Bezig met overnemen…" : "Neem over"}</button>
          </div>
        </div>
      )}
      {error && <p role="alert" data-testid="vg-quick-error" className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}
    </section>
  );
};
